/**
 * ResourceLink Component
 *
 * Displays a link to external online resources/materials.
 * Features:
 * - External link icon
 * - Opens in new tab (target="_blank")
 * - Security attributes (rel="noopener noreferrer")
 * - Full accessibility
 * - Visual indicator for external links
 *
 * @component
 */

import React from 'react';
import type { ResourceLinkProps } from '../types/method-fields';

export const ResourceLink = React.forwardRef<HTMLDivElement, ResourceLinkProps>(
  ({ title, url, external = true, testId }, ref) => {
    return (
      <div
        ref={ref}
        className="resource-link bg-teal-50 dark:bg-teal-900/20 rounded-xl p-4 mt-6 border border-teal-200 dark:border-teal-800"
        data-testid={testId}
      >
        {/* Title */}
        <h4 className="resource-link-title text-sm font-semibold text-zinc-900 dark:text-zinc-100 mb-2 flex items-center gap-2">
          <span aria-hidden="true">📎</span>
          {title}
        </h4>

        {/* Link */}
        <a
          href={url}
          target={external ? '_blank' : '_self'}
          rel={external ? 'noopener noreferrer' : undefined}
          className="resource-link-anchor inline-flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 rounded px-2 py-1"
          aria-label={`${title} - Opens in new tab`}
        >
          {/* Link Icon */}
          <svg
            className="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1"
            />
          </svg>

          <span>Open Resource</span>

          {/* External Link Icon */}
          {external && (
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
              />
            </svg>
          )}
        </a>

        {/* Helper text */}
        <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-1 ml-7">
          {external ? 'Opens in new tab' : 'Opens in current tab'}
        </p>
      </div>
    );
  }
);

ResourceLink.displayName = 'ResourceLink';
